import React from 'react';
import { motion } from 'motion/react';
import { ArrowRight, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

const Landing = () => {
  // Update page title
  React.useEffect(() => {
    document.title = "Kodamalas Krushi Foundation - Transforming Lives Through Technology";
  }, []);
  const springConfig = {
    type: "spring",
    damping: 80,
    stiffness: 400,
    mass: 1
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex items-center justify-center">
      {/* Elegant Background with Advanced Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A0F] via-[#0F0F1A] to-[#1A1A2E]">
        {/* Animated background particles */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[var(--brand-primary)] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float"></div>
          <div className="absolute top-3/4 right-1/4 w-80 h-80 bg-[var(--brand-purple)] rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-float" style={{animationDelay: '2s'}}></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-[var(--brand-coral)] rounded-full mix-blend-multiply filter blur-3xl opacity-15 animate-float" style={{animationDelay: '4s'}}></div>
        </div>

        {/* Enhanced Geometric patterns */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 border border-[var(--brand-primary)] rotate-45 animate-spin-slow"></div>
          <div className="absolute bottom-20 right-20 w-24 h-24 border border-[var(--brand-purple)] rotate-12 animate-pulse"></div>
          <div className="absolute top-1/3 right-1/3 w-16 h-16 bg-gradient-to-r from-[var(--brand-coral)] to-transparent rounded-full animate-ping"></div>
          
          {/* Additional geometric shapes */}
          <div className="absolute top-1/2 left-20 w-20 h-20 border-2 border-[var(--brand-primary)] rounded-full animate-spin-slow" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-32 left-1/4 w-12 h-12 bg-[var(--brand-purple)] transform rotate-45 animate-bounce" style={{animationDelay: '2s'}}></div>
          <div className="absolute top-20 right-1/4 w-8 h-8 border border-[var(--brand-coral)] animate-pulse" style={{animationDelay: '3s'}}></div>
          
          {/* Hexagon shapes */}
          <div className="absolute top-3/4 left-1/3 w-16 h-16 border border-[var(--brand-primary)] transform rotate-12 animate-float" style={{
            clipPath: 'polygon(30% 0%, 70% 0%, 100% 50%, 70% 100%, 30% 100%, 0% 50%)',
            animationDelay: '1.5s'
          }}></div>
          <div className="absolute bottom-40 right-1/3 w-12 h-12 border border-[var(--brand-purple)] transform rotate-45 animate-float" style={{
            clipPath: 'polygon(30% 0%, 70% 0%, 100% 50%, 70% 100%, 30% 100%, 0% 50%)',
            animationDelay: '2.5s'
          }}></div>
        </div>

        {/* Subtle dot pattern */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }}></div>

        {/* Tech Circuit Lines */}
        <div className="absolute inset-0 opacity-20">
          {/* Horizontal lines */}
          <div className="absolute top-1/4 left-1/4 w-32 h-px bg-[var(--brand-primary)] animate-pulse"></div>
          <div className="absolute top-3/4 right-1/4 w-40 h-px bg-[var(--brand-purple)] animate-pulse" style={{animationDelay: '2s'}}></div>
          <div className="absolute bottom-1/3 left-1/3 w-24 h-px bg-[var(--brand-coral)] animate-pulse" style={{animationDelay: '4s'}}></div>
          
          {/* Vertical lines */}
          <div className="absolute left-1/4 top-1/4 h-16 w-px bg-[var(--brand-primary)] animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute right-1/3 bottom-1/4 h-20 w-px bg-[var(--brand-purple)] animate-pulse" style={{animationDelay: '3s'}}></div>
          
          {/* Diagonal tech lines */}
          <div className="absolute top-0 right-0 w-32 h-32 border-r border-b border-[var(--brand-coral)] opacity-30 animate-pulse" style={{animationDelay: '2.5s'}}></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 border-l border-t border-[var(--brand-primary)] opacity-30 animate-pulse" style={{animationDelay: '1.5s'}}></div>
        </div>

        {/* DNA Helix Pattern */}
        <div className="absolute top-20 right-10 opacity-15">
          <div className="w-2 h-40 relative">
            <div className="absolute top-0 left-0 w-2 h-2 bg-[var(--brand-primary)] rounded-full animate-bounce"></div>
            <div className="absolute top-8 right-0 w-2 h-2 bg-[var(--brand-purple)] rounded-full animate-bounce" style={{animationDelay: '0.5s'}}></div>
            <div className="absolute top-16 left-0 w-2 h-2 bg-[var(--brand-coral)] rounded-full animate-bounce" style={{animationDelay: '1s'}}></div>
            <div className="absolute top-24 right-0 w-2 h-2 bg-[var(--brand-primary)] rounded-full animate-bounce" style={{animationDelay: '1.5s'}}></div>
            <div className="absolute top-32 left-0 w-2 h-2 bg-[var(--brand-purple)] rounded-full animate-bounce" style={{animationDelay: '2s'}}></div>
          </div>
        </div>

        {/* Brain Network Pattern */}
        <div className="absolute bottom-32 left-10 opacity-20">
          <div className="relative w-32 h-32">
            {/* Central node */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-[var(--brand-primary)] rounded-full animate-pulse"></div>
            
            {/* Connection nodes */}
            <div className="absolute top-2 left-4 w-2 h-2 bg-[var(--brand-purple)] rounded-full animate-pulse" style={{animationDelay: '0.5s'}}></div>
            <div className="absolute top-4 right-2 w-2 h-2 bg-[var(--brand-coral)] rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
            <div className="absolute bottom-4 left-2 w-2 h-2 bg-[var(--brand-primary)] rounded-full animate-pulse" style={{animationDelay: '1.5s'}}></div>
            <div className="absolute bottom-2 right-4 w-2 h-2 bg-[var(--brand-purple)] rounded-full animate-pulse" style={{animationDelay: '2s'}}></div>
            
            {/* Connection lines */}
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 128 128">
              <line x1="64" y1="64" x2="20" y2="16" stroke="currentColor" strokeWidth="1" className="text-[var(--brand-primary)] opacity-50 animate-pulse"/>
              <line x1="64" y1="64" x2="108" y2="20" stroke="currentColor" strokeWidth="1" className="text-[var(--brand-purple)] opacity-50 animate-pulse"/>
              <line x1="64" y1="64" x2="12" y2="112" stroke="currentColor" strokeWidth="1" className="text-[var(--brand-coral)] opacity-50 animate-pulse"/>
              <line x1="64" y1="64" x2="116" y2="108" stroke="currentColor" strokeWidth="1" className="text-[var(--brand-primary)] opacity-50 animate-pulse"/>
            </svg>
          </div>
        </div>
      </div>

      {/* Content Container */}
      <div className="relative z-10 text-center px-6 md:px-8 lg:px-12 max-w-5xl mx-auto">
        {/* Decorative tech rings around content */}
        <div className="absolute -top-20 -left-20 w-40 h-40 border border-[var(--brand-primary)] rounded-full opacity-10 animate-spin-slow"></div>
        <div className="absolute -top-10 -left-10 w-20 h-20 border border-[var(--brand-primary)] rounded-full opacity-20 animate-pulse-ring"></div>
        <div className="absolute -bottom-20 -right-20 w-40 h-40 border border-[var(--brand-purple)] rounded-full opacity-10 animate-spin-slow" style={{animationDelay: '2s'}}></div>
        <div className="absolute -bottom-10 -right-10 w-20 h-20 border border-[var(--brand-purple)] rounded-full opacity-20 animate-pulse-ring" style={{animationDelay: '1s'}}></div>
        {/* Logo/Icon Animation */}
        <motion.div
          initial={{ opacity: 0, scale: 0.5, rotate: -10 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ ...springConfig, delay: 0.2 }}
          className="mb-12 flex justify-center"
        >
          <div className="relative">
            <div className="w-24 h-24 bg-[var(--brand-primary)] rounded-full flex items-center justify-center shadow-2xl animate-pulse-glow">
              <Sparkles size={40} className="text-white animate-shimmer" />
            </div>
            <div className="absolute inset-0 w-24 h-24 bg-[var(--brand-primary)] rounded-full blur-xl opacity-30 animate-pulse"></div>
          </div>
        </motion.div>

        {/* Main Heading */}
        <motion.h1
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springConfig, delay: 0.4 }}
          className="text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-8 tracking-tight"
          style={{
            textShadow: '0 0 50px rgba(0, 208, 132, 0.3)'
          }}
        >
          Kodamalas Krushi
          <br />
          <span className="text-5xl md:text-6xl lg:text-7xl">Foundation</span>
        </motion.h1>

        {/* Caption */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springConfig, delay: 0.6 }}
          className="mb-12"
        >
          <p className="text-2xl md:text-3xl text-gray-300 mb-4 font-light leading-relaxed">
            Transforming Lives Through Technology
          </p>
          <p className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            An innovative organization harnessing the power of artificial intelligence and cutting-edge technology 
            to create meaningful change in child development and societal growth across the globe.
          </p>
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springConfig, delay: 0.8 }}
        >
          <Link to="/home">
            <button className="group relative px-12 py-6 text-xl font-semibold text-white bg-[var(--brand-primary)] rounded-full overflow-hidden shadow-2xl hover:shadow-[0_0_40px_rgba(0,208,132,0.5)] transition-all duration-500 transform hover:scale-105 hover:rotate-1">
              {/* Button shine effect */}
              <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transform -skew-x-12 group-hover:animate-shimmer transition-opacity duration-300"></div>
              
              {/* Button content */}
              <span className="relative flex items-center gap-3">
                Let's Explore
                <ArrowRight size={24} className="group-hover:translate-x-2 transition-transform duration-300" />
              </span>
              
              {/* Glow effect */}
              <div className="absolute inset-0 bg-[var(--brand-primary)] blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
            </button>
          </Link>
        </motion.div>

        {/* Enhanced Floating elements */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 1 }}
          className="absolute inset-0 pointer-events-none"
        >
          {/* Small particles */}
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-[var(--brand-primary)] rounded-full animate-float opacity-60"></div>
          <div className="absolute top-3/4 right-1/4 w-3 h-3 bg-[var(--brand-purple)] rounded-full animate-float opacity-40" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-1/3 left-1/3 w-1 h-1 bg-[var(--brand-coral)] rounded-full animate-float opacity-80" style={{animationDelay: '2s'}}></div>
          <div className="absolute top-1/2 right-1/2 w-2 h-2 bg-white rounded-full animate-float opacity-30" style={{animationDelay: '3s'}}></div>
          
          {/* Tech-inspired shapes */}
          <div className="absolute top-10 left-1/3 w-4 h-4 bg-[var(--brand-primary)] animate-float opacity-50 transform rotate-45" style={{animationDelay: '0.5s'}}></div>
          <div className="absolute bottom-20 right-1/3 w-3 h-3 border border-[var(--brand-purple)] animate-float opacity-60 transform rotate-12" style={{animationDelay: '1.5s'}}></div>
          <div className="absolute top-1/3 left-20 w-2 h-6 bg-gradient-to-b from-[var(--brand-coral)] to-transparent animate-float opacity-40" style={{animationDelay: '2.5s'}}></div>
          <div className="absolute bottom-1/4 left-1/2 w-6 h-2 bg-gradient-to-r from-[var(--brand-primary)] to-transparent animate-float opacity-50" style={{animationDelay: '3.5s'}}></div>
          
          {/* Binary code effect */}
          <div className="absolute top-16 right-1/4 text-[var(--brand-primary)] text-xs opacity-30 animate-float font-mono" style={{animationDelay: '1s'}}>
            01001001
          </div>
          <div className="absolute bottom-32 left-1/4 text-[var(--brand-purple)] text-xs opacity-30 animate-float font-mono" style={{animationDelay: '2s'}}>
            11010110
          </div>
          <div className="absolute top-2/3 right-20 text-[var(--brand-coral)] text-xs opacity-30 animate-float font-mono" style={{animationDelay: '3s'}}>
            10110001
          </div>
          
          {/* Additional geometric floating shapes */}
          <div className="absolute top-1/6 right-1/6 w-3 h-3 border-2 border-[var(--brand-primary)] rounded-full animate-float opacity-40" style={{animationDelay: '0.8s'}}></div>
          <div className="absolute bottom-1/6 left-1/6 w-4 h-4 bg-[var(--brand-purple)] transform rotate-45 animate-float opacity-30" style={{
            clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
            animationDelay: '1.8s'
          }}></div>
          <div className="absolute top-1/5 left-1/5 w-2 h-8 bg-gradient-to-b from-[var(--brand-coral)] via-transparent to-[var(--brand-primary)] animate-float opacity-25" style={{animationDelay: '2.8s'}}></div>
        </motion.div>
      </div>

      {/* Bottom fade effect */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0A0A0F] to-transparent"></div>
    </div>
  );
};

export default Landing;